import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    Session,
    ProblemGroup,
    Problem,
    SessionWithGroups,
    getSessionWithGroups,
    getProblemsBySession,
    ProblemGroupWithProblems,
    getActiveSession
} from 'services/sessionService';

interface SessionStudioContextType {
    selectedSessionId: number | null;
    selectSession: (id: number | null) => void;
    sessionData: SessionWithGroups | null;
    problemGroups: ProblemGroupWithProblems[];
    activeSessionId: number | null;
    isLoading: boolean;
    isSaving: boolean;
    lastSaved: Date | null;
    error: string | null;
    clearError: () => void;
    refreshSession: () => Promise<void>;
    createNewSession: (title: string, description?: string, startDate?: string, endDate?: string) => Promise<Session>;
    addProblemGroup: (sessionId: number) => Promise<ProblemGroup>;
    addProblem: (groupId: number) => Promise<Problem>;
    updateProblemGroup: (groupId: number, updates: Partial<ProblemGroup>) => Promise<ProblemGroup>;
    updateProblem: (problemId: number, updates: Partial<Problem>) => Promise<Problem>;
    deleteProblem: (problemId: number) => Promise<void>;
    deleteProblemGroup: (groupId: number) => Promise<void>;
}

const SessionStudioContext = createContext<SessionStudioContextType | undefined>(undefined);

// Extract a human-readable message from an axios/network error.
const getErrorMessage = (err: unknown, fallback: string): string => {
    const error = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
    return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
};

export const useSessionStudio = () => {
    const context = useContext(SessionStudioContext);
    if (!context) {
        throw new Error('useSessionStudio must be used within a SessionStudioProvider');
    }
    return context;
};

export const SessionStudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
    const [sessionData, setSessionData] = useState<SessionWithGroups | null>(null);
    const [problemGroups, setProblemGroups] = useState<ProblemGroupWithProblems[]>([]);
    const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved] = useState<Date | null>(null);
    const [error, setError] = useState<string | null>(null);

    const clearError = useCallback(() => setError(null), []);

    // Fetch active session on mount
    useEffect(() => {
        const fetchActiveSession = async () => {
            try {
                const activeSession = await getActiveSession();
                setActiveSessionId(activeSession?.session?.id || null);
            } catch (error) {
                console.error('Error fetching active session:', error);
            }
        };
        fetchActiveSession();
    }, []);

    const fetchSessionDetails = useCallback(async (sessionId: number) => {
        setIsLoading(true);
        try {
            const [sessionResult, problemsResult] = await Promise.all([
                getSessionWithGroups(sessionId),
                getProblemsBySession(sessionId)
            ]);

            setSessionData(sessionResult);

            // Merge problems into groups
            const groupsWithProblems = sessionResult.problemGroups.map(group => ({
                ...group,
                problems: problemsResult.filter(p => p.groupId === group.id)
            }));

            setProblemGroups(groupsWithProblems);
        } catch (error) {
            console.error('Error fetching session details:', error);
            // Handle error (maybe add an error state to context)
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedSessionId) {
            fetchSessionDetails(selectedSessionId);
        } else {
            setSessionData(null);
            setProblemGroups([]);
        }
    }, [selectedSessionId, fetchSessionDetails]);

    const refreshSession = async () => {
        if (selectedSessionId) {
            await fetchSessionDetails(selectedSessionId);
        }
    };

    const createNewSession = async (title: string, description?: string, startDate?: string, endDate?: string) => {
        setIsSaving(true);
        try {
            // Create a default session structure
            const newSession = await import('services/sessionService').then(m => m.createSession({
                title,
                description: description || '',
                startDate: startDate || new Date().toISOString(),
                endDate: endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // +30 days
                acceptingEnrollment: false,
                enrollmentType: 'individual',
                basePrice: 0
            }));

            setSelectedSessionId(newSession.id);
            await fetchSessionDetails(newSession.id);
            return newSession;
        } catch (err) {
            console.error('Error creating session:', err);
            setError(getErrorMessage(err, 'Failed to create session'));
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const addProblemGroup = async (sessionId: number) => {
        setIsSaving(true);
        try {
            const newGroup = await import('services/sessionService').then(m => m.createProblemGroup({
                sessionId,
                title: `Week ${problemGroups.length + 1}`,
                description: 'New Week Description',
                groupOrder: problemGroups.length + 1
            }));

            await fetchSessionDetails(sessionId);
            return newGroup;
        } catch (err) {
            console.error('Error creating group:', err);
            setError(getErrorMessage(err, 'Failed to add group'));
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const addProblem = async (groupId: number) => {
        if (selectedSessionId === null) {
            throw new Error('No session selected');
        }
        setIsSaving(true);
        try {
            const newProblem = await import('services/sessionService').then(m => m.createProblem({
                sessionId: selectedSessionId,
                groupId,
                problemStatement: 'New Problem',
                difficultyLevel: 1,
                // Seed a placeholder answer (edited inline by the teacher). The
                // backend requires a non-empty solution for open-ended problems,
                // so an empty string here would 400. Mirrors the 'New Problem'
                // placeholder used for the statement.
                correctSolution: 'TBD',
                imagePath: null,
                problemType: 'open_ended'
            }));

            await fetchSessionDetails(selectedSessionId);
            return newProblem;
        } catch (err) {
            console.error('Error creating problem:', err);
            setError(getErrorMessage(err, 'Failed to add problem'));
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const updateProblemGroup = async (groupId: number, updates: Partial<ProblemGroup>) => {
        if (selectedSessionId === null) {
            throw new Error('No session selected');
        }
        setIsSaving(true);
        try {
            const updatedGroup = await import('services/sessionService').then(m => m.updateProblemGroup(groupId, updates));
            await fetchSessionDetails(selectedSessionId);
            return updatedGroup;
        } catch (err) {
            console.error('Error updating group:', err);
            setError(getErrorMessage(err, 'Failed to update group'));
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const updateProblem = async (problemId: number, updates: Partial<Problem>) => {
        if (selectedSessionId === null) {
            throw new Error('No session selected');
        }
        setIsSaving(true);
        try {
            const updatedProblem = await import('services/sessionService').then(m => m.updateProblem(problemId, updates));
            await fetchSessionDetails(selectedSessionId);
            return updatedProblem;
        } catch (err) {
            console.error('Error updating problem:', err);
            setError(getErrorMessage(err, 'Failed to update problem'));
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const deleteProblem = async (problemId: number) => {
        if (selectedSessionId === null) {
            throw new Error('No session selected');
        }
        setIsSaving(true);
        try {
            await import('services/sessionService').then(m => m.deleteProblem(problemId));
            await fetchSessionDetails(selectedSessionId);
        } catch (err) {
            console.error('Error deleting problem:', err);
            setError(getErrorMessage(err, 'Failed to delete problem'));
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const deleteProblemGroup = async (groupId: number) => {
        if (selectedSessionId === null) {
            throw new Error('No session selected');
        }
        setIsSaving(true);
        try {
            await import('services/sessionService').then(m => m.deleteProblemGroup(groupId));
            await fetchSessionDetails(selectedSessionId);
        } catch (err) {
            console.error('Error deleting group:', err);
            setError(getErrorMessage(err, 'Failed to delete group'));
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    const value = {
        selectedSessionId,
        selectSession: setSelectedSessionId,
        sessionData,
        problemGroups,
        activeSessionId,
        isLoading,
        isSaving,
        lastSaved,
        error,
        clearError,
        refreshSession,
        createNewSession,
        addProblemGroup,
        addProblem,
        updateProblemGroup,
        updateProblem,
        deleteProblem,
        deleteProblemGroup
    };

    return (
        <SessionStudioContext.Provider value={value}>
            {children}
        </SessionStudioContext.Provider>
    );
};
